import React, {
    useContext,
    useEffect,
    useMemo,
    useRef
} from "react";
import {
    Route,
    Switch,
    useHistory
} from "react-router-dom";

import AuthenticatedUserContext from "./context/AuthenticatedUserContext";
import useTracking from "./hooks/useTracking";
import PrivateRoute from "./components/PrivateRoute";
import Header from "./components/Header";

import Home from "./pages/initial/Home";
import LoginPage from "./pages/initial/LoginPage";
import RegisterPage from "./pages/initial/RegisterPage";
import ForgotPassword from "./pages/initial/ForgotPassword";
import Discover from "./pages/Discover";
import TabBar from "./components/navigation/TabBar";
import Account from "./pages/Account";
import EventPage from "./pages/EventPage";
import EventParticipationSubmissionWrapper from "./pages/eventParticipationSubmission/EventParticipationSubmissionWrapper";
import AcceptInvite from "./pages/AcceptInvite";
import InvitePaymentFinalize from "./pages/InvitePaymentFinalize";
import ResetPassword from "./pages/initial/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import EventParticipationSubmissionEmbedPage from "./pages/public/EventParticipationSubmissionEmbedPage";

function DRVRRouter() {
    const {
        user,
        initialLoadPathname,
        resetInitialLoadPathname,
    } = useContext(AuthenticatedUserContext);
    const prevUserRef = useRef();
    const history = useHistory();

    useEffect(() => {
        if(user !== null && prevUserRef.current === null) {
            if(initialLoadPathname !== null) {
                history.push(initialLoadPathname);
                resetInitialLoadPathname();
            }
        }
        prevUserRef.current = user;
    }, [user, initialLoadPathname, resetInitialLoadPathname, history]);

    const unauthenticatedTarget = useMemo(() => {
        // Directly send users to the login page if they were trying to access a private route while not authenticated.
        if(initialLoadPathname !== null) {
            return "/login";
        }
        return "/";
    }, [initialLoadPathname]);

    useTracking();

    return (
        <Switch>
            <PrivateRoute path="/" target="/discover" exact authenticated={ user === null }>
                <Home/>
            </PrivateRoute>
            <PrivateRoute path="/login" target="/discover" authenticated={ user === null }>
                <LoginPage/>
            </PrivateRoute>
            <PrivateRoute path="/register" target="/discover" authenticated={ user === null }>
                <RegisterPage/>
            </PrivateRoute>
            <PrivateRoute path="/forgot-password" target="/discover" authenticated={ user === null }>
                <ForgotPassword/>
            </PrivateRoute>

            <PrivateRoute path="/discover" target={ unauthenticatedTarget } authenticated={ user !== null }>
                <Discover/>
                <TabBar/>
            </PrivateRoute>
            <PrivateRoute path="/account" target={ unauthenticatedTarget } authenticated={ user !== null }>
                <Account/>
                <TabBar/>
            </PrivateRoute>
            
            <PrivateRoute 
                path="/event/:eventId/participation-submission" 
                target={ unauthenticatedTarget }
                authenticated={ user !== null }
            >
                <EventParticipationSubmissionWrapper/>
            </PrivateRoute>

            <PrivateRoute
                path={["/event/:eventId/:page", "/event/:eventId"]}
                target={ unauthenticatedTarget }
                authenticated={ user !== null }
            >
                <EventPage/>
                <TabBar/>
            </PrivateRoute>

            <Route path="/eps-embed/:eventId">
                <EventParticipationSubmissionEmbedPage/>
            </Route>

            <Route path="/accept-invite/:token">
                <AcceptInvite/>
            </Route>
            <Route path="/invite-payment/:paymentToken">
                <InvitePaymentFinalize/>
            </Route>

            <Route path="/password-reset/:token">
                <ResetPassword/>
            </Route>

            <Route path="/privacyverklaring/clean">
                <PrivacyPolicy clean/>
            </Route>
            <Route path="/privacyverklaring">
                <Header/>
                <PrivacyPolicy/>
            </Route>

            <Route path="/">
                <Header/>
                <div className="text-center">
                    <h1>404 Not Found</h1>
                    <p>Deze pagina bestaat niet</p>
                </div>
            </Route>
        </Switch>
    );
}

export default React.memo(DRVRRouter);
